import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Loader2,
  Play,
  FileWarning,
  XCircle,
  Zap,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Paginador } from '@/shared/ui/Paginador';
import type { Emision, EstadoDeEmision } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { AsistenteDeEmision } from './AsistenteDeEmision';
import { PanelDeCorreccion } from './PanelDeCorreccion';
import { ReporteDeEmisiones } from './ReporteDeEmisiones';

const ESTADOS: Record<EstadoDeEmision, { etiqueta: string; clase: string; icono: typeof Play }> = {
  en_proceso: {
    etiqueta: 'En proceso',
    clase: 'bg-amber-50 text-amber-700 ring-amber-200',
    icono: Loader2,
  },
  completada: {
    etiqueta: 'Completada',
    clase: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    icono: CheckCircle2,
  },
  fallida: {
    etiqueta: 'Fallida',
    clase: 'bg-red-50 text-red-600 ring-red-200',
    icono: XCircle,
  },
};

/** Una emisión por mes: con seis por página se ve medio año de un vistazo. */
const EMISIONES_POR_PAGINA = 6;

export function PaginaEmisionMensual() {
  const clienteDeConsultas = useQueryClient();

  const [confirmando, setConfirmando] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [pestana, setPestana] = useState<'mensual' | 'reemision' | 'reporte'>('mensual');
  const [pagina, setPagina] = useState(0);

  const historial = useQuery({
    queryKey: ['emisiones'],
    queryFn: () => api.get<Emision[]>('/emisiones'),
  });

  const emisiones = historial.data ?? [];

  const tramo = emisiones.slice(pagina * EMISIONES_POR_PAGINA, (pagina + 1) * EMISIONES_POR_PAGINA);

  return (
    <>
      <EncabezadoDePagina
        icono={CalendarClock}
        titulo="Emisión mensual"
        subtitulo="EL LOTE QUE COBRA UN MES: UNA FACTURA POR CLIENTE CON TODOS SUS PREDIOS"
        acciones={
          pestana === 'reemision' ? undefined : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              title="Emitir un periodo"
              aria-label="Emitir un periodo"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Play className="size-5" aria-hidden />
            </button>
          )
        }
      />

      <div className="mb-5 flex gap-6 border-b border-armazon-200">
        {(
          [
            ['mensual', 'Emisión mensual', Zap],
            ['reemision', 'Corrección manual', FileWarning],
            ['reporte', 'Reporte', BarChart3],
          ] as const
        ).map(([clave, etiqueta, Icono]) => (
          <button
            key={clave}
            type="button"
            onClick={() => setPestana(clave)}
            aria-current={pestana === clave ? 'page' : undefined}
            className={`-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 text-[13px] font-semibold uppercase tracking-wide transition-colors ${
              pestana === clave
                ? 'border-marca-600 text-marca-700'
                : 'border-transparent text-armazon-400 hover:border-armazon-300 hover:text-armazon-600'
            }`}
          >
            <Icono className="size-4 shrink-0" aria-hidden />
            {etiqueta}
          </button>
        ))}
      </div>

      {pestana === 'reemision' && <PanelDeCorreccion emisiones={emisiones} />}

      {pestana === 'reporte' && <ReporteDeEmisiones periodos={emisiones.map((e) => e.periodo)} />}

      {pestana === 'mensual' &&
        (historial.isPending ? (
          <Cargando
            titulo="Cargando las emisiones"
            detalle="Trayendo el historial de lotes"
            alto="h-64"
          />
        ) : emisiones.length === 0 ? (
          <div className="rounded-2xl border border-armazon-200 bg-white p-12 text-center">
            <CalendarClock className="mx-auto mb-3 size-8 text-armazon-300" aria-hidden />
            <p className="text-sm uppercase text-armazon-400">
              Todavía no se ha emitido ningún mes
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
            <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
              <span className="w-6 shrink-0" aria-hidden />
              <span className="w-36 shrink-0">Periodo</span>
              <span className="w-32 shrink-0">Vence</span>
              <span className="w-24 shrink-0 text-right">Facturas</span>
              <span className="w-24 shrink-0 text-right">Omitidos</span>
              <span className="w-24 shrink-0 text-right">Con error</span>
              <span className="min-w-0 flex-1 text-right">Total</span>
              <span className="w-28 shrink-0 text-center">Estado</span>
            </div>

            <div className="divide-y divide-armazon-100">
              {tramo.map((e) => (
                <FilaDeEmision
                  key={e.id}
                  emision={e}
                  abierta={abierta === e.id}
                  alAlternar={() => setAbierta(abierta === e.id ? null : e.id)}
                />
              ))}
            </div>

            <Paginador
              pagina={pagina}
              porPagina={EMISIONES_POR_PAGINA}
              total={emisiones.length}
              quePagina="emisiones"
              alCambiar={setPagina}
            />
          </div>
        ))}

      <AsistenteDeEmision
        abierto={confirmando}
        yaEmitidos={emisiones.filter((e) => e.estado === 'completada').map((e) => e.periodo)}
        alCerrar={() => setConfirmando(false)}
        alTerminar={() => void clienteDeConsultas.invalidateQueries({ queryKey: ['emisiones'] })}
      />
    </>
  );
}

function FilaDeEmision({
  emision,
  abierta,
  alAlternar,
}: {
  emision: Emision;
  abierta: boolean;
  alAlternar: () => void;
}) {
  const estado = ESTADOS[emision.estado];
  const Icono = estado.icono;
  const tieneDetalle = emision.incidencias.length > 0;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60">
        <button
          type="button"
          onClick={alAlternar}
          disabled={!tieneDetalle}
          title={tieneDetalle ? 'Ver las incidencias' : 'Sin incidencias'}
          aria-label={`Ver las incidencias de ${emision.periodo}`}
          aria-expanded={abierta}
          className="grid size-6 shrink-0 place-items-center rounded text-armazon-400 transition-colors hover:bg-armazon-100 disabled:opacity-25"
        >
          {abierta ? (
            <ChevronDown className="size-4" aria-hidden />
          ) : (
            <ChevronRight className="size-4" aria-hidden />
          )}
        </button>

        <div className="w-36 shrink-0">
          <p className="importe text-sm font-semibold text-armazon-800">{emision.periodo}</p>
          {/* El mes en letra: «2026-09» hay que descifrarlo, y en una lista de nueve periodos
              seguidos es lo que se busca con la vista. */}
          <p className="text-[11px] font-bold uppercase tracking-wide text-marca-700">
            {enMesYAnio(emision.periodo)}
          </p>
          <p className="text-[10px] uppercase text-armazon-400">
            Emitida {formatearCorto(emision.fechaEmision)}
          </p>
        </div>

        <div className="w-32 shrink-0">
          <p className="importe text-xs text-armazon-700">
            {formatearCorto(emision.fechaVencimiento)}
          </p>
          {emision.duracionSegundos != null && (
            <p className="text-[10px] uppercase text-armazon-400">{emision.duracionSegundos} s</p>
          )}
        </div>

        <p className="importe w-24 shrink-0 text-right text-sm font-semibold text-armazon-800">
          {emision.facturasGeneradas.toLocaleString('es-EC')}
        </p>

        <p
          className="importe w-24 shrink-0 text-right text-sm text-armazon-500"
          title="Predios que ya tenían factura del periodo, normalmente por facturación anticipada"
        >
          {emision.omitidos.toLocaleString('es-EC')}
        </p>

        <p
          className={`importe w-24 shrink-0 text-right text-sm ${
            emision.conError > 0 ? 'font-semibold text-amber-600' : 'text-armazon-300'
          }`}
        >
          {emision.conError.toLocaleString('es-EC')}
        </p>

        <div className="min-w-0 flex-1 text-right">
          <p className="importe text-sm font-bold text-armazon-800">
            ${emision.montoTotal.toFixed(2)}
          </p>
          {emision.montoDescuento > 0 && (
            <p className="text-[10px] uppercase text-armazon-400">
              ${emision.montoDescuento.toFixed(2)} exonerados
            </p>
          )}
        </div>

        <div className="flex w-28 shrink-0 justify-center">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ring-1 ${estado.clase}`}
            title={`Ejecutada por ${emision.ejecutadoPor}`}
          >
            <Icono
              className={`size-3 ${emision.estado === 'en_proceso' ? 'animate-spin' : ''}`}
              aria-hidden
            />
            {estado.etiqueta}
          </span>
        </div>
      </div>

      {abierta && tieneDetalle && (
        <div className="border-t border-armazon-100 bg-armazon-50/40 px-6 py-4">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-armazon-500">
            {emision.conError.toLocaleString('es-EC')} predios no se pudieron cobrar
          </p>

          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {emision.incidencias.map((incidencia, i) => (
              <li
                key={i}
                className="importe rounded border border-armazon-200 bg-white px-3 py-1.5 text-[11px] uppercase tracking-wide text-armazon-700"
              >
                {incidencia}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

/** «2026-09» se lee «Septiembre 2026». Si llega algo que no es un periodo, se deja como vino. */
function enMesYAnio(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];
  return nombre === undefined ? periodo : `${nombre} ${anio}`;
}

function formatearCorto(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
